import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useClient, useQuery } from 'urql';
import { timeSlotsQuery } from '../../graphql/services';
import { Service } from './useServiceSelection';

interface TimeGroups {
  morning: string[];
  afternoon: string[];
  evening: string[];
}

interface DateTimeOption {
  id: string;
  date: Date;
  time: string;
  formattedDateTime: string; // Original date time string from API
}

interface UseDateTimeDataProps {
  selectedServices: Service[];
  selectedTimeZone: string;
  selectedLocation: any;
  initialDate?: Date | null;
}

export const useDateTimeData = ({
  selectedServices,
  selectedTimeZone,
  selectedLocation,
   initialDate,
}: UseDateTimeDataProps) => {
  // Get urql client for manual queries
  const client = useClient();

  // Shared state
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate ?? null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDateTimeOption, setSelectedDateTimeOption] = useState<DateTimeOption | null>(null);
  const [dateTimeOptions, setDateTimeOptions] = useState<DateTimeOption[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [isLoadingAllTimeSlots, setIsLoadingAllTimeSlots] = useState(false);

  // Format helpers
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeFromUTC = (timeString: string): string => {
    const dateTime = DateTime.fromISO(timeString).setZone(selectedTimeZone);
    return dateTime.toFormat('HH:mm');
  };

  const formatDisplayDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Generate dates for the next 14 days
  useEffect(() => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    setAvailableDates(dates);
  }, []);

  // Standard query for a single date
  const [result, reexecuteQuery] = useQuery({
    query: timeSlotsQuery,
    pause: !selectedDate,
    requestPolicy: 'network-only',
    variables: {
      date: selectedDate ? formatDate(selectedDate) : '',
      serviceIds: selectedServices.map((service) => service.id),
      locationId: selectedLocation !== null ? selectedLocation.id : null
    },
  });

  const { data, fetching, error } = result;

  // Fetch time slots when date changes (for standard view)
  useEffect(() => {
    if (selectedDate) {

      reexecuteQuery();
      setSelectedTime(null);
    }
  }, [selectedDate, reexecuteQuery]);

  // Group time slots into morning, afternoon, evening
  const groupTimeSlots = (utcSlots: string[]): TimeGroups => {
    const groups: TimeGroups = {
      morning: [],
      afternoon: [],
      evening: []
    };

    const now = DateTime.now().setZone(selectedTimeZone);

    utcSlots.forEach(utcSlot => {
      // Convert UTC slot to local time
      const slotInLocalTime = DateTime.fromISO(utcSlot).setZone(selectedTimeZone);

      // Check if slot is in the past
      if (slotInLocalTime < now) return;

      // Format time as HH:mm
      const timeStr = slotInLocalTime.toFormat('HH:mm');
      const hour = slotInLocalTime.hour;

      // Group by time of day
      if (hour < 12) {
        groups.morning.push(timeStr);
      } else if (hour < 17) {
        groups.afternoon.push(timeStr);
      } else {
        groups.evening.push(timeStr);
      }
    });

    // Sort each group
    groups.morning.sort();
    groups.afternoon.sort();
    groups.evening.sort();

    return groups;
  };

  // Process time slots if data is available (for standard view)
  const timeGroups = data?.getAvailableSlots ? groupTimeSlots(data.getAvailableSlots) : null;
  const hasAvailableSlots = timeGroups ? (
    (timeGroups.morning && timeGroups.morning.length > 0) ||
    (timeGroups.afternoon && timeGroups.afternoon.length > 0) ||
    (timeGroups.evening && timeGroups.evening.length > 0)
  ) : false;

  // Helper function to fetch time slots for a specific date using urql client
  const fetchTimeSlotsForDate = async (date: Date): Promise<string[]> => {
    const variables = {
      date: formatDate(date),
      serviceIds: selectedServices.map((service) => service.id),
      locationId: selectedLocation !== null ? selectedLocation.id : null
    };

    try {
      // Use urql client directly for consistency
      const result = await client.query(timeSlotsQuery, variables).toPromise();
      return result.data?.getAvailableSlots || [];
    } catch (error) {
      console.error("Error fetching time slots:", error);
      return [];
    }
  };

  // Fetch time slots for multiple dates (for compact view)
  const fetchAllTimeSlots = async (limit = 12) => {
    setIsLoadingAllTimeSlots(true);
    try {
      const allOptions: DateTimeOption[] = [];

      // Create a queue of dates to process
      const dateQueue = [...availableDates];

      // Process dates sequentially
      while (dateQueue.length > 0 && allOptions.length < limit) {
        const currentDate = dateQueue.shift();
        if (!currentDate) continue;

        const slots = await fetchTimeSlotsForDate(currentDate);

        if (slots && slots.length > 0) {
          // Convert UTC time slots to local time and create options
          slots.forEach((slot: string) => {
            const localTime = formatTimeFromUTC(slot);

            allOptions.push({
              id: `dt-${currentDate.toISOString()}-${localTime}`,
              date: new Date(currentDate),
              time: localTime,
              formattedDateTime: slot
            });
          });
        }
      }

      setDateTimeOptions(allOptions);
      return allOptions;
    } finally {
      setIsLoadingAllTimeSlots(false);
    }
  };

  // For compact view - handle selection of a combined date/time option
  const handleDateTimeOptionSelect = (option: DateTimeOption) => {
    setSelectedDateTimeOption(option);
    setSelectedDate(option.date);
    setSelectedTime(option.time);
  };

  return {
    // Standard view state
    selectedDate,
    selectedTime,
    availableDates,
    data,
    fetching,
    error,
    timeGroups,
    hasAvailableSlots,

    // Compact view state
    dateTimeOptions,
    selectedDateTimeOption,
    isLoadingAllTimeSlots,

    // Actions
    setSelectedDate,
    setSelectedTime,
    handleDateTimeOptionSelect,
    fetchAllTimeSlots,

    // Helpers
    formatDate,
    formatTimeFromUTC,
    formatDisplayDate
  };
};
